import type { UserData } from './types'
import { ProfileModelConstants } from '@/utils/constants/profile.model.constants'

export const useUserStore = defineStore('user', () => {
  const userData = ref<UserData | null>(null)
  const regionalId = ref<string>('')

  const sessionDrId = ref<string>('')

  const getId = () => {
    return userData.value?.id ?? null
  }

  const getFullName = computed(() => {
    if (userData.value === null) return ''

    return userData.value.name
  })

  const getProfile = computed(() => {
    if (userData.value === null) return ''

    return userData.value.profile[0]
  })

  const getProfileName = computed(() => {
    if (userData.value === null) return ''

    return userData.value.profile[0].name
  })

  const getPhoto = computed(() => {
    if (userData.value === null || userData.value.photo === '') return ''

    return `${import.meta.env.VITE_AVATAR_BASE_URL}/${userData.value.photo}`
  })

  const isSuperAdmin = computed(() => {
    if (getProfile.value === '') return false

    return (
      getProfile.value.id.toString() ===
      ProfileModelConstants.SUPERADMIN_ID.ID.toString()
    )
  })

  const isAdmin = computed(() => {
    if (getProfile.value === '') return false

    return (
      getProfile.value.id.toString() ===
      ProfileModelConstants.ADMIN_ID.ID.toString()
    )
  })

  const isAdminRegional = computed(() => {
    if (getProfile.value === '') return false

    return (
      getProfile.value.id.toString() ===
      ProfileModelConstants.ADMIN_REGIONAL_ID.ID.toString()
    )
  })

  const setRegional = (departmentId?: string) => {
    if (!departmentId) {
      // Se há um DR de sessão temporário (troca via modal ou switch-department),
      // ele tem prioridade sobre o padrão do banco.
      // Isso garante que chamadas setRegional() em qualquer página não sobrescrevam
      // a troca de DR feita pelo usuário durante a sessão.
      const sessionDr = sessionStorage.getItem('sw-active-dr')
      if (sessionDr) {
        regionalId.value = sessionDr
        return
      }
      const { getRegional } = useUserData()
      regionalId.value = getRegional()
      return
    }
    regionalId.value = departmentId
  }

  /**
   * Seta o DR de sessão do usuário — o DR que ele escolheu para a sessão atual.
   * Diferente do regionalId (que pode ser sobrescrito por páginas para contextos de API),
   * o sessionDrId persiste para exibição no navbar durante toda a navegação.
   */
  const setSessionDr = (departmentId: string) => {
    sessionDrId.value = departmentId
  }

  /**
   * Inicializa o DR de sessão a partir do sessionStorage ou do padrão do banco.
   * Chamado uma vez após loadAuthSession.
   */
  const initSessionDr = () => {
    const sessionDr = sessionStorage.getItem('sw-active-dr')
    if (sessionDr) {
      sessionDrId.value = sessionDr
      return
    }
    const { getRegional } = useUserData()
    sessionDrId.value = getRegional()
  }

  const getRegional = () => {
    if (regionalId.value === '') {
      const { getRegional: getRegionalFromUserData } = useUserData()
      regionalId.value = getRegionalFromUserData()
    }
    return regionalId.value
  }

  const isCoordinator = computed(() => {
    return userData.value?.isCoordinator
  })

  const isSpecialist = computed(() => {
    return userData.value?.isSpecialist
  })

  const isRegionalCoordinator = computed(() => {
    return userData.value?.isRegionalCoordinator
  })

  const isRegionalSpecialist = computed(() => {
    return userData.value?.isRegionalSpecialist
  })

  return {
    userData,
    getId,
    getFullName,
    getProfile,
    getProfileName,
    getPhoto,
    isSuperAdmin,
    isAdmin,
    isAdminRegional,
    isCoordinator,
    regionalId,
    sessionDrId,
    setRegional,
    setSessionDr,
    initSessionDr,
    getRegional,
    isSpecialist,
    isRegionalCoordinator,
    isRegionalSpecialist,
  }
})
